import React, { useState, useEffect, useRef } from "react";
import { Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Tab, Tabs } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { OrderService } from "../../../utils/api.services";
import { playStatusChime } from "../../../utils/customerAudioHelper";
import { getWebSocketURL } from "../../../utils/utils";

const ActiveOrderScreen = ({
  order: initialOrder,
  onOrderCancelled,
  onEditOrder,
  onNewOrder,
  onOrderUpdated,
}) => {
  const navigate = useNavigate();
  const [order, setOrder] = useState(initialOrder);
  const [timeLeft, setTimeLeft] = useState(0);
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [statusNotice, setStatusNotice] = useState("");

  const prevStatusRef = useRef(initialOrder?.status);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const FIVE_MINUTES_SECS = 5 * 60;

  // Sync internal state when parent props update
  useEffect(() => {
    if (initialOrder) {
      setOrder(initialOrder);
      if (
        prevStatusRef.current &&
        initialOrder.status &&
        prevStatusRef.current !== initialOrder.status
      ) {
        // Status changed via prop update
        playStatusChime(initialOrder.status);
        showStatusTransitionNotice(initialOrder.status);
        prevStatusRef.current = initialOrder.status;
      } else if (!prevStatusRef.current && initialOrder.status) {
        prevStatusRef.current = initialOrder.status;
      }
    }
  }, [initialOrder]);

  const showStatusTransitionNotice = (newStatus) => {
    let msg = "";
    switch (String(newStatus).toUpperCase()) {
      case "PREPARING":
        msg = "🍳 Chef is now preparing your delicious food!";
        break;
      case "READY":
        msg = "🔔 Your order is READY! Please pick it up or it will be served soon.";
        break;
      case "SERVED":
        msg = "🍽️ Your order has been served. Enjoy your meal!";
        break;
      case "COMPLETED":
        msg = "✨ Order completed! Thank you for dining with us.";
        break;
      default:
        msg = `Status updated to ${newStatus}`;
        break;
    }
    setStatusNotice(msg);
    setTimeout(() => {
      setStatusNotice("");
    }, 6000);
  };

  // Real-time WebSocket connection to track this specific order
  useEffect(() => {
    const orderId = order?._id;
    if (!orderId) return;

    let isMounted = true;

    const connectOrderWS = () => {
      if (!isMounted) return;
      try {
        const wsUrl = getWebSocketURL();
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          ws.send(
            JSON.stringify({
              type: "JOIN_ORDER",
              orderId: String(orderId),
            })
          );
        };

        ws.onmessage = (event) => {
          if (!isMounted) return;
          try {
            const data = JSON.parse(event.data);
            const { type, payload } = data;
            if (!payload) return;

            const updatedOrder = payload.order || payload;
            const updatedStatus = updatedOrder.status || payload.status;

            if (type === "ORDER_STATUS_CHANGED" && updatedStatus) {
              setOrder((prev) => ({
                ...prev,
                ...updatedOrder,
                status: updatedStatus,
              }));

              if (prevStatusRef.current !== updatedStatus) {
                playStatusChime(updatedStatus);
                showStatusTransitionNotice(updatedStatus);
                prevStatusRef.current = updatedStatus;
              }

              if (onOrderUpdated) onOrderUpdated(updatedOrder);
            } else if (type === "ITEM_STATUS_CHANGED") {
              const { itemId, itemName, itemStatus, order: freshOrder } = payload;
              if (freshOrder) {
                setOrder(freshOrder);
                if (onOrderUpdated) onOrderUpdated(freshOrder);
              } else {
                setOrder((prev) => {
                  if (!prev) return prev;
                  const updateList = (list = []) =>
                    list.map((it) => {
                      const mId = String(it.itemId?._id || it.itemId || it._id);
                      if (mId === String(itemId)) {
                        return { ...it, itemStatus, itemStatusUpdatedAt: new Date() };
                      }
                      return it;
                    });
                  const nextOrder = {
                    ...prev,
                    status: payload.status || prev.status,
                    cartItems: updateList(prev.cartItems),
                    pickUpCartItems: updateList(prev.pickUpCartItems),
                  };
                  if (onOrderUpdated) onOrderUpdated(nextOrder);
                  return nextOrder;
                });
              }

              // Play audio chime with sound
              playStatusChime(itemStatus);

              // Popup alert banner
              let alertMsg = `🔔 ${itemName || "Dish"} is now ${itemStatus}!`;
              if (itemStatus === "SERVED") {
                alertMsg = `🍽️ Your ${itemName || "Dish"} is Served! Enjoy your meal.`;
              } else if (itemStatus === "READY") {
                alertMsg = `🔔 Your ${itemName || "Dish"} is Ready!`;
              } else if (itemStatus === "PREPARING") {
                alertMsg = `🍳 Chef is now preparing your ${itemName || "Dish"}!`;
              }
              setStatusNotice(alertMsg);
              setTimeout(() => {
                setStatusNotice("");
              }, 6000);
            } else if (type === "ORDER_EDITED") {
              setOrder((prev) => ({
                ...prev,
                ...updatedOrder,
              }));
              playStatusChime("PREPARING");
              if (onOrderUpdated) onOrderUpdated(updatedOrder);
            } else if (type === "ORDER_CANCELLED") {
              if (onOrderCancelled) onOrderCancelled();
            }
          } catch (e) {
            console.warn("Error parsing order WebSocket event:", e);
          }
        };

        ws.onclose = () => {
          if (!isMounted) return;
          reconnectTimeoutRef.current = setTimeout(connectOrderWS, 4000);
        };

        ws.onerror = () => {
          try {
            ws.close();
          } catch (e) {}
        };
      } catch (err) {
        if (isMounted) {
          reconnectTimeoutRef.current = setTimeout(connectOrderWS, 5000);
        }
      }
    };

    connectOrderWS();

    return () => {
      isMounted = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {}
      }
    };
  }, [order?._id, onOrderUpdated, onOrderCancelled]);

  // Live timer for 5-minute editing window
  useEffect(() => {
    if (!order || !order.createdAt) return;

    const calculateTimeLeft = () => {
      const createdTime = new Date(order.createdAt).getTime();
      const now = Date.now();
      const elapsed = Math.floor((now - createdTime) / 1000);
      const remaining = Math.max(0, FIVE_MINUTES_SECS - elapsed);
      setTimeLeft(remaining);
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [order?.createdAt]);

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleConfirmCancel = async () => {
    setCancelling(true);
    setCancelError("");
    try {
      const res = await OrderService.cancelOrderCustomer(order._id);
      if (res.status === 200) {
        setCancelModal(false);
        if (onOrderCancelled) onOrderCancelled();
      }
    } catch (err) {
      setCancelError(err?.response?.data?.message || "Failed to cancel order.");
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (String(status || "").toUpperCase()) {
      case "PENDING":
        return <Badge color="warning" className="px-3 py-2 fs-6">⏳ Order Pending</Badge>;
      case "PREPARING":
        return <Badge color="info" className="px-3 py-2 fs-6">🍳 Preparing Your Food</Badge>;
      case "READY":
        return <Badge color="success" className="px-3 py-2 fs-6 animate-pulse">✅ Order Ready!</Badge>;
      case "SERVED":
        return <Badge color="primary" className="px-3 py-2 fs-6">🍽️ Served</Badge>;
      case "PAYMENT_CONFIRMATION":
        return <Badge color="secondary" className="px-3 py-2 fs-6">💸 Payment Review</Badge>;
      case "COMPLETED":
        return <Badge color="success" className="px-3 py-2 fs-6">✨ Order Completed</Badge>;
      default:
        return <Badge color="dark" className="px-3 py-2 fs-6">{status}</Badge>;
    }
  };

  const hasAnyItemServed =
    (order?.cartItems || []).some(
      (it) => String(it.itemStatus || "").toUpperCase() === "SERVED"
    ) ||
    (order?.pickUpCartItems || []).some(
      (it) => String(it.itemStatus || "").toUpperCase() === "SERVED"
    );

  const isOrderServed =
    hasAnyItemServed ||
    ["COMPLETED", "CANCELLED", "SERVED"].includes(
      String(order?.status || "").toUpperCase()
    );

  const isTimerActive = timeLeft > 0 && !isOrderServed;

  const getItemStatusBadge = (status) => {
    const s = String(status || "PREPARING").toUpperCase();
    switch (s) {
      case "PENDING":
        return <Badge color="warning" className="ms-2 px-2 py-1 font-monospace" style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>⏳ Pending</Badge>;
      case "PREPARING":
        return <Badge color="info" className="ms-2 px-2 py-1 font-monospace" style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>🍳 Preparing</Badge>;
      case "READY":
        return <Badge color="success" className="ms-2 px-2 py-1 font-monospace" style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>🔔 Ready</Badge>;
      case "SERVED":
        return <Badge color="primary" className="ms-2 px-2 py-1 font-monospace" style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>🍽️ Served</Badge>;
      case "CANCELLED":
        return <Badge color="danger" className="ms-2 px-2 py-1 font-monospace" style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>❌ Cancelled</Badge>;
      default:
        return null;
    }
  };

  const getOrderTypeDisplay = () => {
    const rawT = String(order.tableNumber || "").trim().toUpperCase();
    const isShopQR = (!order.tableNumber || rawT === "0" || rawT === "NULL" || rawT === "SHOP QR" || rawT === "SHOP_QR");
    const isParcel = (rawT === "PARCEL" || rawT.includes("PARCEL") || order.orderType === "PARCEL");

    if (isParcel) {
      return "📦 Parcel";
    }

    if (isShopQR) {
      return "🛍️ Shop QR";
    }

    const hasDineIn = (order.cartItems?.length || 0) > 0;
    const hasPickUp = (order.pickUpCartItems?.length || 0) > 0;

    if (hasDineIn && hasPickUp) {
      return `🍽️ Dine In (Table ${order.tableNumber}) + 📦 Parcel`;
    }

    return `🪑 Table ${order.tableNumber}`;
  };

  return (
    <div className="active-order-screen p-3 max-w-lg mx-auto" style={{ maxWidth: "600px", width: "100%", margin: "0 auto" }}>
      {/* Real-time Status Change Banner */}
      {statusNotice && (
        <div
          className="alert alert-success d-flex align-items-center justify-content-between shadow-sm p-3 mb-3 rounded-3"
          style={{
            background: "#d1fae5",
            border: "1px solid #10b981",
            color: "#065f46",
            animation: "fadeInDown 0.3s ease-in-out",
          }}
        >
          <div className="fw-bold">{statusNotice}</div>
          <button
            className="btn btn-sm btn-close"
            onClick={() => setStatusNotice("")}
          ></button>
        </div>
      )}

      {/* Top Banner Card */}
      <div className="bg-white rounded-3 shadow-sm p-4 text-center mb-3 border">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-muted small">ORDER TOKEN</span>
          <span className="text-muted small">ORDER TYPE</span>
        </div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="display-5 fw-bold text-primary mb-0">#{order.token || "---"}</h1>
          <span className="badge bg-light text-dark border px-3 py-2 fw-bold fs-6">
            {getOrderTypeDisplay()}
          </span>
        </div>

        <div className="my-3">{getStatusBadge(order.status)}</div>

        {/* 5-Min Timer Section */}
        <div
          className="timer-box p-3 rounded-3 mt-3"
          style={{
            background: isTimerActive ? "#fff8e1" : "#f5f5f5",
            border: isTimerActive ? "1px solid #ffe082" : "1px solid #e0e0e0",
          }}
        >
          {isTimerActive ? (
            <div>
              <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                <span style={{ fontSize: "1.2rem" }}>⏱️</span>
                <span className="fw-bold text-dark">Live Editing Window:</span>
                <span className="badge bg-danger fs-6 px-3 py-1 font-monospace">
                  {formatTimer(timeLeft)}
                </span>
              </div>
              <p className="small text-muted mb-3">
                You can edit or cancel your order while timer is active.
              </p>
              <div className="d-flex justify-content-center gap-2">
                {onEditOrder && (
                  <Button
                    color="outline-primary"
                    size="sm"
                    className="fw-bold px-3"
                    onClick={() => onEditOrder(order)}
                  >
                    ✏️ Edit Order
                  </Button>
                )}
                <Button
                  color="outline-danger"
                  size="sm"
                  className="fw-bold px-3"
                  onClick={() => setCancelModal(true)}
                >
                  ❌ Cancel Order
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-1">
              <span className="badge bg-warning text-dark border p-2 text-wrap fs-6">
                {isOrderServed
                  ? "🍽️ Food has been served. Editing is closed. Please contact our Service Staff."
                  : "⚠️ Editing Window Closed. Please contact our Service Staff."}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Order Summary Card */}
      <div className="bg-white rounded-3 shadow-sm p-4 mb-3 border">
        <h5 className="fw-bold mb-3">Order Details</h5>
        <Tabs defaultActiveKey="dineIn" id="activeOrderTabs" className="mb-3 underline-tabs">
          <Tab eventKey="dineIn" title={`Dine In (${order.cartItems?.length || 0})`}>
            {order.cartItems && order.cartItems.length > 0 ? (
              <div className="list-group list-group-flush mb-2">
                {order.cartItems.map((item, idx) => (
                  <div key={idx} className="list-group-item d-flex justify-content-between align-items-center px-0 flex-wrap gap-1">
                    <div className="d-flex align-items-center flex-wrap gap-1">
                      <span className="fw-bold">{item.quantity} × </span>
                      <span>{item.itemId?.name || item.name || "Item"}</span>
                      {getItemStatusBadge(item.itemStatus)}
                    </div>
                    <span className="fw-bold">₹{(item.quantity * item.price).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted small my-2">No Dine In items in this order.</p>
            )}
          </Tab>
          <Tab eventKey="pickUp" title={`Pick Up (${order.pickUpCartItems?.length || 0})`}>
            {order.pickUpCartItems && order.pickUpCartItems.length > 0 ? (
              <div className="list-group list-group-flush mb-2">
                {order.pickUpCartItems.map((item, idx) => (
                  <div key={idx} className="list-group-item d-flex justify-content-between align-items-center px-0 flex-wrap gap-1">
                    <div className="d-flex align-items-center flex-wrap gap-1">
                      <span className="fw-bold">{item.quantity} × </span>
                      <span>{item.itemId?.name || item.name || "Item"}</span>
                      {getItemStatusBadge(item.itemStatus)}
                    </div>
                    <span className="fw-bold">₹{(item.quantity * item.price).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted small my-2">No Pick Up items in this order.</p>
            )}
          </Tab>
        </Tabs>

        <hr />
        <div className="d-flex justify-content-between align-items-center fw-bold fs-5">
          <span>Total Amount:</span>
          <span className="text-success">₹{order.totalAmount}</span>
        </div>
      </div>

      {/* Footer Navigation Buttons */}
      <div className="d-grid gap-2">
        {order.status !== "COMPLETED" && (
          <Button color="success" size="lg" className="fw-bold py-3 shadow-sm" onClick={() => navigate("/payment")}>
            💳 View Bill & Pay Online
          </Button>
        )}
        {onNewOrder && (
          <Button
            color={order.status === "COMPLETED" ? "primary" : "light"}
            size={order.status === "COMPLETED" ? "lg" : undefined}
            className={`border fw-bold py-3 ${
              order.status === "COMPLETED" ? "text-white shadow" : "text-dark mt-1"
            }`}
            onClick={onNewOrder}
          >
            {order.status === "COMPLETED" ? "✨ Start New Order" : "+ Place Another Order / View Menu"}
          </Button>
        )}
      </div>

      {/* Cancel Order Confirmation Modal */}
      <Modal isOpen={cancelModal} toggle={() => setCancelModal(false)} centered>
        <ModalHeader toggle={() => setCancelModal(false)}>Cancel Order Confirmation</ModalHeader>
        <ModalBody className="text-center p-4">
          <span style={{ fontSize: "2.5rem" }}>❓</span>
          <h5 className="fw-bold mt-2">Are you sure you want to cancel this order?</h5>
          <p className="text-muted small">
            This action cannot be undone once confirmed.
          </p>
          {cancelError && <p className="text-danger small fw-bold">{cancelError}</p>}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-center">
          <Button color="secondary" onClick={() => setCancelModal(false)} disabled={cancelling}>
            No, Keep Order
          </Button>
          <Button color="danger" className="fw-bold px-4" onClick={handleConfirmCancel} disabled={cancelling}>
            {cancelling ? "Cancelling..." : "Yes, Cancel Order"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ActiveOrderScreen;
